import React, { useEffect, useState } from 'react';
import useToken from 'hooks/useToken';
import DatePicker from 'react-datepicker';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import 'react-datepicker/dist/react-datepicker.css';
import { Card, Col, Row, Form } from 'react-bootstrap';

import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import classNames from 'classnames';
import SkeletonTable from '../../../../components/common/SkeletonTable'
import { useToast } from 'context/providers/ToastProvider';

const ListaPagos = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const { token, setToken } = useToken();
    const { showErrorToast } = useToast();
    const [rfc, setRFC] = useState([]);
    const [selectedRfc, setSelectedRfc] = useState("");
    const [dateQuarter, setDateQuarter] = useState(new Date());

    //Columnas de la Tabla
    const defaultColumnWidth = 150; // Ancho uniforme por defecto
    const defaultMaxWidth = '180px'; // Máximo ancho uniforme para truncado

    const columns = [
        {
            accessor: 'none',
            Header: '',
            Cell: rowData => {
                const { idFacturaPagoEmitido } = rowData.row.original;
                const url = "/contabilidad/centro-xml/pagos-emitidos/visualizacion/" + idFacturaPagoEmitido;

                return (
                    <div className={rowData.row.original.bIsActive === false ? 'factura-inactiva-flag d-flex align-items-center gap-2' : 'd-flex align-items-center gap-2'}>
                        <a href={url} target="_blank" rel="noopener noreferrer">
                            <img title="Ver información" src="/img/ver20.png" alt="ver" />
                        </a>
                        {rowData.row.original.bIsActive === false && (
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip>
                                        Esta factura no está siendo tomada en cuenta
                                    </Tooltip>
                                }
                            >
                                <div
                                    style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        backgroundColor: 'red',
                                        display: 'inline-block'
                                    }}
                                ></div>
                            </OverlayTrigger>
                        )}
                    </div>
                );
            }
        },
        {
            accessor: 'vSerie',
            Header: 'Serie',
            headerProps: { className: 'align-middle text-center bg-light' },
            cellProps: { className: 'align-middle text-center text-truncate', style: { maxWidth: defaultMaxWidth } },
            width: defaultColumnWidth,
            Cell: rowData => {
                const { idFacturaPagoEmitido, vSerie } = rowData.row.original;
                const url = "/contabilidad/centro-xml/pagos-emitidos/visualizacion/" + idFacturaPagoEmitido;

                return (
                    <OverlayTrigger placement="top" overlay={<Tooltip>{vSerie}</Tooltip>}>
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={applyTextColorByActive(rowData.row, 'd-inline-block text-primary text-truncate')}
                            style={{ fontSize: '0.9em', maxWidth: '100%' }}
                        >
                            {vSerie}
                        </a>
                    </OverlayTrigger>
                );
            }
        },
        {
            accessor: 'vFolio',
            Header: 'Folio',
            headerProps: { className: 'align-middle text-center bg-light' },
            cellProps: { className: 'align-middle text-center' },
            width: defaultColumnWidth,
            Cell: rowData => {
                const { idFacturaPagoEmitido, vFolio } = rowData.row.original;
                const url = "/contabilidad/centro-xml/pagos-emitidos/visualizacion/" + idFacturaPagoEmitido;

                return (
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={applyTextColorByActive(rowData.row, 'fw-medium text-primary')}
                        style={{ fontSize: '0.9em' }}
                    >
                        {vFolio}
                    </a>
                );
            }
        },
        {
            accessor: 'dFechaEmision',
            Header: 'Fecha Emisión',
            sortable: true,
            headerProps: { className: 'align-middle text-center bg-light' },
            cellProps: { className: 'align-middle text-center' },
            width: defaultColumnWidth,
            Cell: rowData => (
                <span className={applyTextColorByActive(rowData.row, '')} style={{ fontSize: '0.9em' }}>
                    {new Date(rowData.row.original.dFechaEmision).toLocaleDateString('es-MX', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    })}
                </span>
            )
        },
        {
            accessor: 'dFechaTimbrado',
            Header: 'Fecha de certificación',
            sortable: true,
            headerProps: { className: 'align-middle text-center bg-light' },
            cellProps: { className: 'align-middle text-center' },
            width: defaultColumnWidth,
            Cell: rowData => (
                <span className={applyTextColorByActive(rowData.row, '')} style={{ fontSize: '0.9em' }}>
                    {new Date(rowData.row.original.dFechaTimbrado).toLocaleDateString('es-MX', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    })}
                </span>
            )
        },
        {
            accessor: 'vRfcEmisor',
            Header: 'RFC Emisor',
            headerProps: { className: 'align-middle text-center bg-light' },
            cellProps: { className: 'align-middle text-center' },
            width: defaultColumnWidth,
            Cell: rowData => (
                <span className={applyTextColorByActive(rowData.row, 'fw-medium text-primary')} style={{ fontSize: '0.9em' }}>
                    {rowData.row.original.vRfcEmisor}
                </span>
            )
        },
        {
            accessor: 'vRazonSocialEmisor',
            Header: 'Razón Social Emisor',
            headerProps: { className: 'align-middle text-center bg-light' },
            cellProps: { className: 'align-middle text-truncate', style: { maxWidth: defaultMaxWidth } },
            width: defaultColumnWidth,
            Cell: rowData => (
                <OverlayTrigger placement="top" overlay={<Tooltip>{rowData.row.original.vRazonSocialEmisor}</Tooltip>}>
                    <span className={applyTextColorByActive(rowData.row, 'fw-medium d-inline-block text-truncate')} style={{ fontSize: '0.9em', maxWidth: '100%' }}>
                        {rowData.row.original.vRazonSocialEmisor}
                    </span>
                </OverlayTrigger>
            )
        },
        {
            accessor: 'vRfcReceptor',
            Header: 'RFC Receptor',
            headerProps: { className: 'align-middle text-center bg-light' },
            cellProps: { className: 'align-middle text-center' },
            width: defaultColumnWidth,
            Cell: rowData => (
                <span className={applyTextColorByActive(rowData.row, 'fw-medium text-primary')} style={{ fontSize: '0.9em' }}>
                    {rowData.row.original.vRfcReceptor}
                </span>
            )
        },
        {
            accessor: 'vRazonSocialReceptor',
            Header: 'Razón Social Receptor',
            headerProps: { className: 'align-middle text-center bg-light' },
            cellProps: { className: 'align-middle text-truncate', style: { maxWidth: defaultMaxWidth } },
            width: defaultColumnWidth,
            Cell: rowData => (
                <OverlayTrigger placement="top" overlay={<Tooltip>{rowData.row.original.vRazonSocialReceptor}</Tooltip>}>
                    <span className={applyTextColorByActive(rowData.row, 'fw-medium d-inline-block text-truncate')} style={{ fontSize: '0.9em', maxWidth: '100%' }}>
                        {rowData.row.original.vRazonSocialReceptor}
                    </span>
                </OverlayTrigger>
            )
        },
        {
            accessor: 'vFormaPago',
            Header: 'FP',
            headerProps: { className: 'align-middle text-center bg-light' },
            cellProps: { className: 'align-middle text-truncate', style: { maxWidth: defaultMaxWidth } },
            width: defaultColumnWidth,
            Cell: rowData => (
                <OverlayTrigger placement="top" overlay={<Tooltip>{rowData.row.original.vFormaPago}</Tooltip>}>
                    <span className={applyTextColorByActive(rowData.row, 'fw-medium d-inline-block text-truncate')} style={{ fontSize: '0.9em', maxWidth: '100%' }}>
                        {rowData.row.original.vFormaPago}
                    </span>
                </OverlayTrigger>
            )
        },
        {
            accessor: 'vCuentaBancaria',
            Header: 'Cta Banc.',
            headerProps: { className: 'align-middle text-center bg-light' },
            cellProps: { className: 'align-middle text-center text-truncate', style: { maxWidth: defaultMaxWidth } },
            width: defaultColumnWidth,
            Cell: rowData => (
                <OverlayTrigger placement="top" overlay={<Tooltip>{rowData.row.original.vCuentaBancaria}</Tooltip>}>
                    <span className={applyTextColorByActive(rowData.row, 'fw-medium d-inline-block text-truncate')} style={{ fontSize: '0.9em', maxWidth: '100%' }}>
                        {rowData.row.original.vCuentaBancaria}
                    </span>
                </OverlayTrigger>
            )
        },
        {
            accessor: 'combinedMoneda',
            Header: 'Moneda',
            headerProps: { className: 'align-middle text-center bg-light' },
            cellProps: { className: 'align-middle text-center' },
            width: defaultColumnWidth,
            Cell: rowData => (
                <span className={applyTextColorByActive(rowData.row, 'fw-medium')} style={{ fontSize: '0.9em' }}>
                    {rowData.row.original.vMoneda}
                </span>
            )
        },
        {
            accessor: 'dMontoTotalPago',
            Header: 'Monto Total',
            headerProps: { className: 'align-middle text-end bg-light' },
            cellProps: { className: 'align-middle text-end' },
            width: defaultColumnWidth,
            Cell: rowData => (
                <span className={applyTextColorByActive(rowData.row, 'fw-bold')} style={{ fontSize: '0.9em' }}>
                    {parseFloat(rowData.row.original.dMontoTotalPago).toLocaleString('es-MX', {
                        style: 'currency',
                        currency: 'MXN'
                    })}
                </span>
            )
        },
        {
            accessor: 'dFechaPago',
            Header: 'Fecha de Pago',
            headerProps: { className: 'align-middle text-center bg-light' },
            cellProps: { className: 'align-middle text-center' },
            width: defaultColumnWidth,
            Cell: rowData => {
                const rawFecha = rowData.row.original?.dFechaPago;
                return (
                    <span className={applyTextColorByActive(rowData.row, '')} style={{ fontSize: '0.9em' }}>
                        {rawFecha ? new Date(rawFecha).toLocaleDateString('es-mx', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Sin fecha'}
                    </span>
                );
            }
        }
    ];


    /**
     *  Obtiene una vez los pagos emitidos 
     */
    useEffect(() => {
        ObtienePagosEmitidos();
    }, [selectedRfc]);

    /**
    * Se ejecuta la primera vez
    */
    useEffect(() => {
        obtenerRfcReceptor();
    }, []);


    /**
     * Funcion para obtener los rfc para el filtro
     */
    const obtenerRfcReceptor = async () => {
        setLoading(true);

        //console.log("Informacion enviada al endpoint",data)
        const response = await fetch("/api/FacturasEmitidas/ObtenerRFCReceptor",
            {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            })
            .catch((error) => {
                showErrorToast('Hubo un problema con la peticion Fetch:' + error);
                //console.log('Hubo un problema con la peticion Fetch:' + error);

            });

        if (response.ok) {
            //console.log("response: ", response);
            const data = await response.json();
            //console.log("data: ", data);

            if (data.iResponseType == 1) {

                var ObjDetalles = JSON.parse(data.detalles);

                setRFC(ObjDetalles.table2);

                setSelectedRfc(ObjDetalles.table2[0].vRFC);


            } else {
                showErrorToast(data.vMessage);
            }

        }
        else {
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
            }
        }
    };

    /**
     * Función para manejar el cambio de fecha
     */
    const handleDateChange = (date) => {
        setDateQuarter(date);
        sessionStorage.setItem("selectMes", date);
        ObtienePagosEmitidos();
    };

    /**
     *  Obtiene datos Pagos Emitidos 
     */
    const ObtienePagosEmitidos = async () => {
        setLoading(true);

        var date_ = sessionStorage.getItem("selectMes") == null ? dateQuarter : sessionStorage.getItem("selectMes");

        setDateQuarter(new Date(date_));

        var month = new Date(date_).getMonth() + 1;
        var year = new Date(date_).getFullYear();

        // Asegura que el mes tenga siempre 2 dígitos (ej. 02, 03, ..., 12)
        var formattedMonth = month < 10 ? '0' + month : '' + month;

        const data = {
            year: '' + year,            // como string (si así lo necesita tu API)
            month: formattedMonth,      // siempre en formato "MM"
            rfc: selectedRfc
        };

        console.log("fecha enviada", data)

        const response = await fetch("/api/PagosEmitidos/GetPagosEmitidos", {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });

        if (response.ok) {

            console.log("==== OBTENER DATOS PAGOS EMITIDOS ====");

            const data = await response.json();
            console.log(data, "Pagos Emitidos")


            if (data.iResponseType == 1) {


                var ObjDetalles = JSON.parse(data.detalles);

                console.log("obtenerDatosPagosEmitidos", ObjDetalles.table2);

                setData(ObjDetalles.table2);


            } else {
                showErrorToast(data.vMessage);
            }
            setLoading(false);

        }
        else {
            setLoading(false);
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
            }
        }
    };




    const applyTextColorByActive = (row, baseClass = '') => {
        return classNames(baseClass, {
            'text-danger': row.original.bIsActive === false
            // No pongas text-dark aquí
        });
    };

    return (
        <>
            <AdvanceTableWrapper
                columns={columns}
                data={data}
                sortable
                pagination
                perPage={15}
            >
                <Card className="mb-3">
                    <Card.Header className="pb-0">
                        <Row className="justify-content-between align-items-end mb-3">
                            {/* Contenedor principal de los filtros */}
                            <Col xs={12} md={9}>
                                <Row className="g-3 align-items-end"> {/* g-3 para espaciado entre columnas */}
                                    {/* Grupo DatePicker */}
                                    <Col xs={12} sm={4} md={3}>
                                        <div className="text-start">
                                            <Form.Label className="text-700 mb-2 d-block">Mes</Form.Label>
                                        </div>
                                        <DatePicker
                                            selected={dateQuarter}
                                            onChange={handleDateChange}
                                            dateFormat="MMMM yyyy - QQQ"
                                            className="form-control w-100"
                                            showMonthYearPicker
                                        />
                                    </Col>
                                    {/* Grupo RFC Emisor */}
                                    <Col xs={12} sm={4} md={3}>
                                        <div className="text-start">
                                            <Form.Label className="text-700 mb-2 d-block">RFC Emisor</Form.Label>
                                        </div>
                                        <Form.Select
                                            size="xs"
                                            value={selectedRfc}
                                            onChange={(e) => setSelectedRfc(e.target.value)}
                                            className="w-100"
                                        >
                                            {rfc.map((item, index) => (
                                                <option key={index} value={item.vRFC}>
                                                    {item.vRFC}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                </Row>
                            </Col>
                            {/* Barra de búsqueda (derecha) */}
                            <Col xs={12} md={3} className="mt-3 mt-md-0">
                                <AdvanceTableSearchBox
                                    style={{ maxWidth: '200px' }}
                                    table
                                />
                            </Col>
                        </Row>
                    </Card.Header>
                    <Card.Body className="p-0">
                        {
                            loading ? (
                                <SkeletonTable
                                    columnNames={['', 'Serie', 'Folio', 'Fecha E', 'Fecha C...', 'RFC Emisor', 'Razon S. Emi', 'RFC Receptor', 'Razon S. Emisor', 'FP', 'Cta. Banc.', 'Moneda', 'Monto Total', 'Fecha de P']}
                                    rows={11}
                                />
                            ) : (
                                <AdvanceTable
                                    table
                                    headerClassName="bg-200 text-900 text-nowrap align-middle"
                                    rowClassName="align-middle white-space-nowrap"
                                    tableProps={{
                                        size: 'sm',
                                        striped: true,
                                        className: 'fs--2 mb-0 overflow-hidden'
                                    }}
                                />
                            )
                        }
                    </Card.Body>
                    <Card.Footer>
                        <AdvanceTableFooter
                            rowCount={data.length}
                            table
                            rowInfo
                            navButtons
                            rowsPerPageSelection
                            nextButtonProps={{ style: { color: 'blue' } }}
                        />
                    </Card.Footer>
                </Card>
            </AdvanceTableWrapper>
        </>
    );

}

export default ListaPagos;